/* global axios */

const accountIdFromRoute = () => {
  if (window.location.pathname.includes('/app/accounts')) {
    return window.location.pathname.split('/')[3];
  }
  return '';
};

const base = () => `/api/v1/accounts/${accountIdFromRoute()}/aceleres`;

const requireTeamId = teamId => {
  if (!teamId)
    throw new Error('teamId é obrigatório para configurar IA e Rodízios');
  return teamId;
};

const settingsUrl = teamId =>
  `/api/v1/accounts/${accountIdFromRoute()}/teams/${requireTeamId(
    teamId
  )}/aceleres/ai_rodizios_settings`;

const withTeamId = (url, teamId) =>
  `${url}${url.includes('?') ? '&' : '?'}team_id=${encodeURIComponent(
    requireTeamId(teamId)
  )}`;

const captainConfigurationUrl = teamId =>
  `/api/v1/accounts/${accountIdFromRoute()}/teams/${teamId}/aceleres/captain_configuration`;

const automationConfigurationsUrl = teamId =>
  `/api/v1/accounts/${accountIdFromRoute()}/teams/${requireTeamId(
    teamId
  )}/automation_configurations`;

// Aceleres — Fase 1: anexa ?team_id= (ou &) nas chamadas de Contexto
// (Materiais/Eventos/Cursos) quando abertas no modal de um time. Sem teamId,
// cai no escopo conta (página global). O controller filtra por team_id.
const withTeam = (url, teamId) =>
  teamId
    ? `${url}${url.includes('?') ? '&' : '?'}team_id=${encodeURIComponent(teamId)}`
    : url;

export default {
  // ---------- Settings ----------
  getSettings(teamId) {
    return axios.get(settingsUrl(teamId));
  },
  updateSettings(payload, teamId) {
    return axios.patch(settingsUrl(teamId), payload);
  },
  getCaptainConfiguration(teamId) {
    return axios.get(captainConfigurationUrl(teamId));
  },
  updateCaptainConfiguration(teamId, payload) {
    return axios.patch(captainConfigurationUrl(teamId), payload);
  },
  listAutomationConfigurations(teamId) {
    return axios.get(automationConfigurationsUrl(teamId));
  },
  createAutomationConfiguration(teamId, payload) {
    return axios.post(automationConfigurationsUrl(teamId), payload);
  },
  updateAutomationConfiguration(teamId, configurationId, payload) {
    return axios.patch(
      `${automationConfigurationsUrl(teamId)}/${configurationId}`,
      payload
    );
  },
  deleteAutomationConfiguration(teamId, configurationId) {
    return axios.delete(
      `${automationConfigurationsUrl(teamId)}/${configurationId}`
    );
  },

  // ---------- Materiais (Contexto da IA) ----------
  listMateriais(section = null, teamId = null) {
    const q = section ? `?section=${encodeURIComponent(section)}` : '';
    return axios.get(withTeam(`${base()}/ai_materiais${q}`, teamId));
  },
  getMaterial(id, teamId = null) {
    return axios.get(withTeam(`${base()}/ai_materiais/${id}`, teamId));
  },
  createMaterial(material, teamId = null) {
    return axios.post(withTeam(`${base()}/ai_materiais`, teamId), toMaterialFormData(material));
  },
  updateMaterial(id, material, teamId = null) {
    return axios.patch(withTeam(`${base()}/ai_materiais/${id}`, teamId), toMaterialFormData(material));
  },
  deleteMaterial(id, teamId = null) {
    return axios.delete(withTeam(`${base()}/ai_materiais/${id}`, teamId));
  },

  // ---------- Eventos ----------
  listEventos(teamId = null) {
    return axios.get(withTeam(`${base()}/ai_eventos`, teamId));
  },
  createEvento(evento, teamId = null) {
    return axios.post(withTeam(`${base()}/ai_eventos`, teamId), toEventoFormData(evento));
  },
  updateEvento(id, evento, teamId = null) {
    return axios.patch(withTeam(`${base()}/ai_eventos/${id}`, teamId), toEventoFormData(evento));
  },
  deleteEvento(id, teamId = null) {
    return axios.delete(withTeam(`${base()}/ai_eventos/${id}`, teamId));
  },

  // ---------- Cursos ----------
  listCursos(teamId = null) {
    return axios.get(withTeam(`${base()}/ai_cursos`, teamId));
  },
  createCurso(curso, teamId = null) {
    return axios.post(withTeam(`${base()}/ai_cursos`, teamId), { curso });
  },
  updateCurso(id, curso, teamId = null) {
    return axios.patch(withTeam(`${base()}/ai_cursos/${id}`, teamId), { curso });
  },
  deleteCurso(id, teamId = null) {
    return axios.delete(withTeam(`${base()}/ai_cursos/${id}`, teamId));
  },

  // ---------- Per-Agent Working Hours (team-scoped) ----------
  listAgents(teamId) {
    return axios.get(withTeamId(`${base()}/ai_agent_working_hours`, teamId));
  },
  updateAgentWorkingHours(userId, workingHours, teamId) {
    return axios.put(
      withTeamId(`${base()}/ai_agent_working_hours/${userId}`, teamId),
      {
        working_hours: workingHours,
      }
    );
  },
  resetAgentWorkingHours(userId, teamId) {
    return axios.delete(
      withTeamId(`${base()}/ai_agent_working_hours/${userId}`, teamId)
    );
  },
};

function toMaterialFormData({ title, content, priority, is_active: isActive, source_type: sourceType, section, file }) {
  const fd = new FormData();
  if (title !== undefined) fd.append('material[title]', title || '');
  if (content !== undefined) fd.append('material[content]', content || '');
  if (priority !== undefined) fd.append('material[priority]', priority ?? 0);
  if (isActive !== undefined) fd.append('material[is_active]', isActive ? '1' : '0');
  if (sourceType !== undefined) fd.append('material[source_type]', sourceType || 'text');
  if (section !== undefined) fd.append('material[section]', section || 'zoraxy');
  if (file instanceof File) fd.append('material[file]', file);
  return fd;
}

function toEventoFormData({ name, description, expires_at: expiresAt, is_active: isActive, file }) {
  const fd = new FormData();
  if (name !== undefined) fd.append('evento[name]', name || '');
  if (description !== undefined) fd.append('evento[description]', description || '');
  if (expiresAt !== undefined) fd.append('evento[expires_at]', expiresAt || '');
  if (isActive !== undefined) fd.append('evento[is_active]', isActive ? '1' : '0');
  if (file instanceof File) fd.append('evento[file]', file);
  return fd;
}
